/**
 * DocumentPreviewPanel — uniwersalna podglądarka załącznika kartoteki (DMS-style:
 * pasek narzędzi z zoomem/obrotem/pobraniem/pełnym ekranem + rail miniaturek gdy
 * załączników jest więcej niż jeden).
 *
 * Komponent jest CZYSTO PREZENTACYJNY: dostaje gotową listę `RecordAttachmentDTO`
 * (`@saas/shared`) + id aktywnego załącznika. Wybór KTÓRE załączniki pokazać
 * (all/primary/latest/pole formularza) leży po stronie wołającego — to inny strumień
 * pracy i celowo nie jest tu implementowany.
 *
 * Adaptacja z szablonu SellCall (`apps/admin/src/components/document-preview/
 * DocumentPreviewPanel.tsx` w `ite_workflow365`), NIE kopia 1:1 — różnice celowe:
 *  - Załączniki tego repo NIE mają publicznego URL-a (`RecordAttachmentDTO` celowo
 *    nie eksponuje `fileKey`/URL-a magazynu). Treść pobieramy WYŁĄCZNIE przez
 *    `api.get(buildAttachmentContentUrl(id), { responseType: 'blob' })` (autoryzacja
 *    Bearer w tenant scope idzie nagłówkiem, nie cookie — zwykły `<img src=".../content">`
 *    dostałby 401), a wynikowy blob zamieniamy na `URL.createObjectURL(...)`.
 *  - Zero embedowania w zewnętrznych przeglądarkach Office (MS/Google viewer) — dla
 *    `office`/`video`/`audio`/`unknown` UI pokazuje wyłącznie przycisk pobrania,
 *    nigdy nie osadza (żadnego wycieku URL-i tenanta na serwery stron trzecich).
 *  - Brak integracji specyficznej dla źródłowego repo (podgląd KSeF dla XML) — `xml`
 *    renderuje się jako zwykły iframe z surową treścią.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordAttachmentDTO } from '@saas/shared';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  FileQuestion,
  Loader2,
  Maximize2,
  Minimize2,
  PanelRightClose,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { Overlay } from '@/components/Overlay';
import { api } from '@/lib/api';

import {
  buildAttachmentContentUrl,
  formatFileSize,
  getFileExtension,
  getPreviewKind,
  getPreviewKindIcon,
  isEmbeddablePreviewKind,
  type PreviewKind,
} from './utils';

export type DocumentPreviewPanelMode = 'dock' | 'inline';

export interface DocumentPreviewPanelProps {
  /** Lista załączników do podglądu — kolejność = kolejność rail-a miniaturek. */
  attachments: RecordAttachmentDTO[];
  /** Id aktywnego załącznika. Gdy nie pasuje do żadnej pozycji, panel pokazuje pierwszy. */
  activeAttachmentId: string | null;
  /** Wywoływane po kliknięciu miniaturki / strzałki nawigacji — panel jest kontrolowany. */
  onActiveAttachmentChange?: (attachmentId: string) => void;
  mode?: DocumentPreviewPanelMode;
  showToolbar?: boolean;
  showThumbnails?: boolean;
  defaultZoom?: number;
  /** Wysokość dla trybu `inline` (liczba = px). Ignorowane w `dock` i pełnym ekranie. */
  height?: number | string;
  /** Gdy podane (tryb dock), pokazuje przycisk zwijania w pasku narzędzi. */
  onCollapse?: () => void;
}

interface BlobState {
  url: string | null;
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_BLOB_STATE: BlobState = { url: null, isLoading: false, isError: false };

/**
 * Pobiera treść załącznika jako blob (przez `api` — nagłówki auth dopina interceptor)
 * i wystawia ją jako `blob:` URL. Zwalnia obiekt przy zmianie/odmontowaniu, żeby nie
 * przeciekać pamięci.
 */
function useAttachmentBlobUrl(attachmentId: string | undefined, enabled: boolean): BlobState {
  const [state, setState] = useState<BlobState>(EMPTY_BLOB_STATE);

  useEffect(() => {
    if (!enabled || !attachmentId) {
      setState(EMPTY_BLOB_STATE);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ url: null, isLoading: true, isError: false });

    api
      .get<Blob>(buildAttachmentContentUrl(attachmentId), { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setState({ url: objectUrl, isLoading: false, isError: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, isLoading: false, isError: true });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentId, enabled]);

  return state;
}

/** Wykonuje pobranie pliku: fetch blob → tymczasowy `<a download>` → zwolnienie URL-a. */
async function triggerAttachmentDownload(attachment: RecordAttachmentDTO): Promise<void> {
  const res = await api.get<Blob>(buildAttachmentContentUrl(attachment.id), {
    responseType: 'blob',
  });
  const blobUrl = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = attachment.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
}

export function DocumentPreviewPanel({
  attachments,
  activeAttachmentId,
  onActiveAttachmentChange,
  mode = 'inline',
  showToolbar = true,
  showThumbnails = true,
  defaultZoom = 1,
  height = 600,
  onCollapse,
}: DocumentPreviewPanelProps) {
  const { t } = useTranslation();

  const activeIndex = attachments.findIndex((a) => a.id === activeAttachmentId);
  const safeIndex = activeIndex >= 0 ? activeIndex : 0;
  const active = attachments[safeIndex];
  const hasMany = attachments.length > 1;

  const [zoom, setZoom] = useState(defaultZoom);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  // Pełny ekran odmontowuje pasek w układzie razem z jego przyciskiem i montuje okno w tym samym
  // zatwierdzeniu, więc rdzeń okien nie ma gdzie oddać fokusu (wyzwalacz znika). Po wyjściu
  // z pełnego ekranu sami oddajemy go przyciskowi pełnego ekranu w układzie.
  const fullscreenButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusAfterFullscreenRef = useRef(false);
  const exitFullscreen = () => {
    restoreFocusAfterFullscreenRef.current = true;
    setFullscreen(false);
  };
  useEffect(() => {
    if (fullscreen || !restoreFocusAfterFullscreenRef.current) return;
    restoreFocusAfterFullscreenRef.current = false;
    fullscreenButtonRef.current?.focus();
  }, [fullscreen]);

  // Reset transformacji widoku przy każdej zmianie aktywnego załącznika.
  useEffect(() => {
    setZoom(defaultZoom);
    setRotation(0);
    setDownloadError(false);
  }, [active?.id, defaultZoom]);

  const ext = active ? getFileExtension(active.fileName) : '';
  const kind: PreviewKind = active ? getPreviewKind(active.mimeType, ext) : 'unknown';
  const embeddable = active ? isEmbeddablePreviewKind(kind) : false;
  const content = useAttachmentBlobUrl(active?.id, embeddable);

  const goToIndex = (i: number) => {
    const target = attachments[i];
    if (target) onActiveAttachmentChange?.(target.id);
  };

  const handleDownload = async (attachment: RecordAttachmentDTO) => {
    setDownloadError(false);
    try {
      await triggerAttachmentDownload(attachment);
    } catch {
      setDownloadError(true);
    }
  };

  if (!active) {
    return (
      <div
        className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 p-6 text-center text-text-muted"
        data-testid="document-preview-empty"
      >
        <FileQuestion className="h-10 w-10" aria-hidden="true" />
        <p className="text-sm">{t('attachmentPreview.empty', 'Brak załączników do podglądu')}</p>
      </div>
    );
  }

  const Icon = getPreviewKindIcon(kind, active.mimeType, ext);
  const viewerStyle = {
    transform: `scale(${zoom}) rotate(${rotation}deg)`,
    transformOrigin: 'center center',
  };
  const transformable = kind === 'image';

  const renderViewer = () => {
    if (!embeddable) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
          <Icon className="h-16 w-16 text-text-muted" aria-hidden="true" />
          <p className="max-w-xs break-all text-sm text-text-secondary">{active.fileName}</p>
          <button
            type="button"
            onClick={() => void handleDownload(active)}
            className="flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700"
            data-testid="document-preview-fallback-download"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('attachmentPreview.download', 'Pobierz')}
          </button>
          {downloadError && (
            <p className="text-xs text-error-500">
              {t('attachmentPreview.downloadError', 'Nie udało się pobrać pliku')}
            </p>
          )}
        </div>
      );
    }

    if (content.isLoading) {
      return (
        <div className="flex items-center justify-center text-text-muted">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        </div>
      );
    }

    if (content.isError || !content.url) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-8 text-center text-text-muted">
          <AlertTriangle className="h-10 w-10" aria-hidden="true" />
          <p className="text-sm">
            {t('attachmentPreview.loadError', 'Nie udało się załadować podglądu')}
          </p>
        </div>
      );
    }

    if (kind === 'image') {
      return (
        <img
          src={content.url}
          alt={active.fileName}
          className="max-h-full max-w-full object-contain transition-transform"
          style={viewerStyle}
          data-testid="document-preview-image"
        />
      );
    }

    // pdf / xml / text — iframe z blob URL-em (content-type ustawia backend na streamie).
    return (
      <iframe
        src={content.url}
        title={active.fileName}
        className="h-full w-full border-0 bg-white"
        data-testid="document-preview-iframe"
      />
    );
  };

  const iconBtn =
    'p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-tertiary/70 transition-colors disabled:opacity-30 disabled:pointer-events-none';
  const divider = <span className="mx-1 h-5 w-px bg-border-secondary" />;

  const toolbar = showToolbar && (
    <div
      className="flex h-11 flex-shrink-0 items-center justify-between gap-2 border-b border-border-primary bg-bg-secondary px-3"
      data-testid="document-preview-toolbar"
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-border-primary bg-bg-primary">
          <Icon className="h-4 w-4 text-text-muted" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <div
            className="truncate text-sm font-medium leading-tight text-text-primary"
            title={active.fileName}
          >
            {active.fileName}
          </div>
          <div className="text-[11px] leading-tight text-text-muted">
            {hasMany ? `${safeIndex + 1} / ${attachments.length}` : null}
            {hasMany && active.sizeBytes ? ' · ' : ''}
            {formatFileSize(active.sizeBytes)}
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-0.5">
        {transformable && (
          <>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))}
              className={iconBtn}
              title={t('attachmentPreview.zoomOut', 'Pomniejsz')}
              aria-label={t('attachmentPreview.zoomOut', 'Pomniejsz')}
              data-testid="document-preview-zoom-out"
            >
              <ZoomOut className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="w-9 text-center text-xs tabular-nums text-text-muted">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
              className={iconBtn}
              title={t('attachmentPreview.zoomIn', 'Powiększ')}
              aria-label={t('attachmentPreview.zoomIn', 'Powiększ')}
              data-testid="document-preview-zoom-in"
            >
              <ZoomIn className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className={iconBtn}
              title={t('attachmentPreview.rotate', 'Obróć')}
              aria-label={t('attachmentPreview.rotate', 'Obróć')}
              data-testid="document-preview-rotate"
            >
              <RotateCw className="h-4 w-4" aria-hidden="true" />
            </button>
            {divider}
          </>
        )}
        <button
          type="button"
          onClick={() => void handleDownload(active)}
          className={iconBtn}
          title={t('attachmentPreview.download', 'Pobierz')}
          aria-label={t('attachmentPreview.download', 'Pobierz')}
          data-testid="document-preview-download"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          ref={fullscreenButtonRef}
          type="button"
          onClick={() => (fullscreen ? exitFullscreen() : setFullscreen(true))}
          className={iconBtn}
          title={
            fullscreen
              ? t('attachmentPreview.exitFullscreen', 'Zamknij pełny ekran')
              : t('attachmentPreview.fullscreen', 'Pełny ekran')
          }
          aria-label={
            fullscreen
              ? t('attachmentPreview.exitFullscreen', 'Zamknij pełny ekran')
              : t('attachmentPreview.fullscreen', 'Pełny ekran')
          }
          data-testid="document-preview-fullscreen"
        >
          {fullscreen ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        {onCollapse && !fullscreen && (
          <>
            {divider}
            <button
              type="button"
              onClick={onCollapse}
              className={iconBtn}
              title={t('attachmentPreview.hide', 'Ukryj podgląd')}
              aria-label={t('attachmentPreview.hide', 'Ukryj podgląd')}
              data-testid="document-preview-collapse"
            >
              <PanelRightClose className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  const thumbnails = showThumbnails && hasMany && (
    <div
      className="flex items-center gap-2 overflow-x-auto border-t border-border-primary bg-bg-secondary px-3 py-2"
      data-testid="document-preview-thumbnails"
    >
      {attachments.map((a, i) => (
        <AttachmentThumbnail
          key={a.id}
          attachment={a}
          isActive={i === safeIndex}
          onClick={() => goToIndex(i)}
        />
      ))}
    </div>
  );

  const navOverlay = hasMany && !showThumbnails && (
    <>
      <button
        type="button"
        onClick={() => goToIndex(Math.max(0, safeIndex - 1))}
        disabled={safeIndex === 0}
        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60 disabled:opacity-30"
        aria-label={t('attachmentPreview.previous', 'Poprzedni')}
        data-testid="document-preview-prev"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => goToIndex(Math.min(attachments.length - 1, safeIndex + 1))}
        disabled={safeIndex === attachments.length - 1}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white hover:bg-black/60 disabled:opacity-30"
        aria-label={t('attachmentPreview.next', 'Następny')}
        data-testid="document-preview-next"
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>
      <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-2 py-0.5 text-xs text-white">
        {safeIndex + 1} / {attachments.length}
      </span>
    </>
  );

  const panelBaseClass =
    'flex flex-col overflow-hidden rounded-lg border border-border-primary bg-bg-primary';
  const panelContent = (
    <>
      {toolbar}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto bg-bg-tertiary">
        {renderViewer()}
        {navOverlay}
      </div>
      {thumbnails}
    </>
  );

  // Pełny ekran = okno na wspólnym rdzeniu `Overlay` (portal do <body>, który ucieka spod
  // przetransformowanego przodka, gdzie position:fixed byłby ucięty pod chrome apki). Tło i Esc
  // zamykają pełny ekran (wracamy do widoku w układzie). z-50 jak każde okno: okna otwarte z
  // pełnego ekranu (np. `ConfirmDialog`) są później w DOM, więc leżą nad nim.
  if (fullscreen) {
    return (
      <Overlay
        open
        onClose={exitFullscreen}
        ariaLabel={active.fileName}
        backdropClassName="z-50 bg-black/60 p-4 backdrop-blur-sm"
        panelClassName={`${panelBaseClass} h-full w-full shadow-2xl`}
        panelTestId="document-preview-panel"
      >
        {panelContent}
      </Overlay>
    );
  }

  return (
    <div
      className={`${panelBaseClass} ${mode === 'dock' ? 'h-full' : ''}`}
      style={
        mode === 'dock'
          ? undefined
          : { height: typeof height === 'number' ? `${height}px` : height }
      }
      data-testid="document-preview-panel"
    >
      {panelContent}
    </div>
  );
}

interface AttachmentThumbnailProps {
  attachment: RecordAttachmentDTO;
  isActive: boolean;
  onClick: () => void;
}

/** Miniaturka w rail-u: realny podgląd dla obrazów (lazy blob fetch), ikona dla reszty. */
function AttachmentThumbnail({ attachment, isActive, onClick }: AttachmentThumbnailProps) {
  const ext = getFileExtension(attachment.fileName);
  const kind = getPreviewKind(attachment.mimeType, ext);
  const isImage = kind === 'image';
  const thumb = useAttachmentBlobUrl(attachment.id, isImage);
  const Icon = getPreviewKindIcon(kind, attachment.mimeType, ext);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded border bg-bg-primary transition-all ${
        isActive
          ? 'border-accent-500 ring-2 ring-accent-500/30'
          : 'border-border-primary hover:border-accent-300'
      }`}
      title={attachment.fileName}
      data-testid={`document-preview-thumbnail-${attachment.id}`}
    >
      {isImage && thumb.url ? (
        <img src={thumb.url} alt={attachment.fileName} className="h-full w-full object-cover" />
      ) : (
        <Icon className="h-6 w-6 text-text-muted" aria-hidden="true" />
      )}
    </button>
  );
}

export default DocumentPreviewPanel;
